'use client';

type CheckshopWidgetFrameProps = {
  type: 'badge' | 'reviews';
  title: string;
  locale?: string;
  className?: string;
  height?: number;
  variant?: 'horizontal' | 'sticker';
  position?: 'inline';
  limit?: number;
  minRating?: number;
  compact?: boolean;
  theme?: 'light' | 'dark';
  widgetTitle?: string;
};

const CHECKSHOP_SHOP_ID = 'cmpk1hwes000z7cmr241ig998';
const CHECKSHOP_DOMAIN = 'printen.eu';

function escapeAttr(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function CheckshopWidgetFrame({
  type,
  title,
  locale = 'nl',
  className,
  height,
  variant = 'horizontal',
  position = 'inline',
  limit = 3,
  minRating = 4,
  compact = true,
  theme = 'light',
  widgetTitle = 'Laatste klantreviews',
}: CheckshopWidgetFrameProps) {
  const isReviews = type === 'reviews';
  const frameHeight = height || (isReviews ? 360 : variant === 'sticker' ? 90 : 70);
  const bodyBackground = isReviews ? '#ffffff' : 'transparent';
  const framePadding = isReviews ? 0 : variant === 'sticker' ? 4 : 8;

  const lang = String(locale || 'nl').slice(0, 2).toLowerCase();

  const attrs = isReviews
    ? `data-shop="${CHECKSHOP_SHOP_ID}" data-limit="${limit}" data-min-rating="${minRating}" data-theme="${theme}" data-compact="${compact ? 'true' : 'false'}" data-title="${escapeAttr(widgetTitle)}" data-lang="${escapeAttr(lang)}"`
    : `data-shop="${CHECKSHOP_SHOP_ID}" data-variant="${variant}" data-position="${position}" data-lang="${escapeAttr(lang)}"`;

  const mount = isReviews
    ? `<div data-checkshop-reviews><a href="https://checkshop.eu/shop/${CHECKSHOP_DOMAIN}" target="_blank" rel="noopener noreferrer">Lees onze klantreviews op CheckShop.eu</a></div>`
    : '<div data-checkshop-badge></div>';

  const scriptSrc = isReviews
    ? 'https://checkshop.eu/reviews/v1/loader.js'
    : 'https://checkshop.eu/badge/v1/loader.js?v=20260525-subtle-tab-3';

  const ratingOverride = isReviews
    ? ''
    : `
    <script>
      fetch('https://checkshop.eu/api/v1/badge/${CHECKSHOP_SHOP_ID}')
        .then(function(response) { return response.ok ? response.json() : null; })
        .then(function(data) {
          if (!data || data.error || typeof data.avgRating !== 'number' || data.avgRating <= 0) return;
          var reviewScore = (data.avgRating * 2).toFixed(1);
          var trustScore = typeof data.score === 'number' ? String(data.score) : '';
          var applyRating = function() {
            var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
            var node;
            while ((node = walker.nextNode())) {
              var text = node.nodeValue || '';
              var trimmed = text.trim();
              if (trustScore && trimmed === trustScore) node.nodeValue = text.replace(trustScore, reviewScore);
              if (trimmed === '/100') node.nodeValue = text.replace('/100', '/10');
              if (trustScore && trimmed.indexOf(trustScore + '/100') >= 0) node.nodeValue = text.replace(trustScore + '/100', reviewScore + '/10');
            }
          };
          applyRating();
          new MutationObserver(applyRating).observe(document.body, { childList: true, subtree: true, characterData: true });
        })
        .catch(function() {});
    </script>`;

  const srcDoc = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>
      html, body { margin: 0; background: ${bodyBackground}; overflow: hidden; font-family: Arial, sans-serif; }
      body { box-sizing: border-box; min-height: ${frameHeight}px; padding: ${framePadding}px; display: flex; align-items: center; }
      body > div { width: 100%; }
      a { color: #be123c; font-weight: 700; text-decoration: none; }
    </style>
  </head>
  <body>
    ${mount}
    <script src="${scriptSrc}" ${attrs}></script>
    ${ratingOverride}
  </body>
</html>`;

  return (
    <iframe
      title={title}
      srcDoc={srcDoc}
      className={className}
      style={{ width: '100%', height: frameHeight, border: 0, display: 'block', overflow: 'hidden' }}
      loading="lazy"
      scrolling="no"
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
    />
  );
}
